import { z } from 'zod'

/**
 * 環境變數 Schema
 */
const envSchema = z.object({
    VITE_API_BASE_URL: z.string().url().optional(),
    VITE_APP_TITLE: z.string().optional().default('SGS 檢驗工作流程管理系統'),
    VITE_APP_VERSION: z.string().optional().default('1.0.0'),
    VITE_DEV_MODE: z.string().optional(),
    DEV: z.boolean().optional(),
    PROD: z.boolean().optional(),
    MODE: z.enum(['development', 'production', 'test']).optional(),
})

/**
 * 環境變數類型
 */
export type Env = z.infer<typeof envSchema>

/**
 * 驗證環境變數
 */
export function validateEnv(): Env {
    try {
        const env = envSchema.parse(import.meta.env)
        console.log('✅ 環境變數驗證通過')
        return env
    } catch (error) {
        if (error instanceof z.ZodError) {
            console.error('❌ 環境變數驗證失敗:')
            error.errors.forEach(err => {
                console.error(`  - ${err.path.join('.')}: ${err.message}`)
            })
        }
        throw new Error('環境變數配置錯誤，請檢查 .env 文件')
    }
}

/**
 * 獲取環境變數（已驗證）
 */
let validatedEnv: Env | null = null

export function getEnv(): Env {
    if (!validatedEnv) {
        validatedEnv = validateEnv()
    }
    return validatedEnv
}

/**
 * 檢查是否為開發環境
 */
export function isDevelopment(): boolean {
    return import.meta.env.DEV || import.meta.env.MODE === 'development'
}

/**
 * 檢查是否為生產環境
 */
export function isProduction(): boolean {
    return import.meta.env.PROD || import.meta.env.MODE === 'production'
}

/**
 * 檢查是否為測試環境
 */
export function isTest(): boolean {
    return import.meta.env.MODE === 'test'
}

/**
 * 獲取 API Base URL
 */
export function getApiBaseUrl(): string {
    const env = getEnv()

    if (env.VITE_API_BASE_URL) {
        return env.VITE_API_BASE_URL
    }

    // 開發環境使用相對路徑（通過 Vite proxy）
    if (isDevelopment()) {
        return '/api'
    }

    // 生產環境使用完整 URL
    return 'https://shipyard-inspection-api.arguskao.workers.dev/api'
}

/**
 * 獲取應用標題
 */
export function getAppTitle(): string {
    return getEnv().VITE_APP_TITLE || 'SGS 檢驗工作流程管理系統'
}

/**
 * 獲取應用版本
 */
export function getAppVersion(): string {
    return getEnv().VITE_APP_VERSION || '1.0.0'
}

// 在應用啟動時驗證環境變數
if (typeof window !== 'undefined') {
    try {
        validateEnv()
    } catch (error) {
        console.error('環境變數驗證失敗，應用可能無法正常運行')
    }
}
